import { BusinessLinkStage } from './BusinessLinkStage.js';
import { BusinessProfileStage } from './BusinessProfileStage.js';
import { VideoChoiceStage } from './VideoChoiceStage.js';
import { ProductImageStage } from './ProductImageStage.js';
import { RemadeVideoStage } from './RemadeVideoStage.js';
import { useEffect, useRef, useState, type CSSProperties, type DragEvent } from 'react';
import type { Language } from '../../i18n/translations.js';
import { DEMO_COPY } from './demo-copy.js';
import { useDemoMedia } from './useDemoMedia.js';

export function GuidedDemo({
  language,
  suspended,
  onInteract,
  onRequestAccess,
}: {
  readonly language: Language;
  readonly suspended: boolean;
  readonly onInteract: () => void;
  readonly onRequestAccess: (from: HTMLElement) => void;
}) {
  const c = DEMO_COPY[language];
  const [step, setStep] = useState(0);
  const [furthest, setFurthest] = useState(0);
  const [selected, setSelected] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [view, setView] = useState('result');
  const shell = useRef<HTMLDivElement>(null);
  const media = useDemoMedia(step, suspended);
  const focusNext = useRef(false);
  useEffect(() => {
    if (!focusNext.current) return;
    focusNext.current = false;
    const heading = shell.current?.querySelector<HTMLElement>(
      `#stage-${String(step)} h3`,
    );
    heading?.focus({ preventScroll: true });
    shell.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, [step]);
  const go = (next: number) => {
    onInteract();
    media.pause();
    if (next === 4) setSelected(true);
    focusNext.current = true;
    setStep(next);
    setFurthest((previous) => Math.max(previous, next));
  };
  const restart = () => {
    media.reset();
    setSelected(false);
    setFurthest(0);
    setView('result');
    go(0);
  };
  const startDrag = (event: DragEvent<HTMLButtonElement>) => {
    event.dataTransfer.setData('application/x-mouja-demo-product', 'cookie');
    event.dataTransfer.effectAllowed = 'copy';
  };
  const dragOver = (event: DragEvent<HTMLDivElement>) => {
    if (!event.dataTransfer.types.includes('application/x-mouja-demo-product')) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
    setDragging(true);
  };
  const dropProduct = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (event.dataTransfer.getData('application/x-mouja-demo-product') === 'cookie')
      setSelected(true);
  };
  return (
    <section
      className="wrap demo-section mw-demo"
      id="demo"
      aria-labelledby="demo-title"
    >
      <div className="section-head">
        <div>
          <p className="eyebrow">
            <i className="signal" aria-hidden="true"></i>
            <span>{c.tryMouja}</span>
          </p>
          <h2 id="demo-title">{c.seeYourProductInTheScene}</h2>
        </div>
        <p className="demo-note" id="demo-disclosure">
          {c.preparedExampleNoLiveAnalysisOr}
        </p>
      </div>
      <div
        className="demo-shell mw-shell"
        aria-describedby="demo-disclosure"
        style={{ '--demo-progress': `${String((step + 1) * 20)}%` } as CSSProperties}
        ref={shell}
      >
        <div className="mw-toolbar">
          <div className="mw-app-name">
            <span className="cs-px3" aria-hidden="true">
              <i></i>
              <i></i>
              <i></i>
            </span>
            <strong>{'Mouja'}</strong>
            <span className="mw-slash" aria-hidden="true">
              {'/'}
            </span>
            <span>{c.workspace}</span>
          </div>
          <div className="mw-tools">
            <button
              className="mw-utility"
              id="restart"
              type="button"
              aria-label={c.restartTheDemo}
              onClick={restart}
            >
              <span aria-hidden="true">{'↺'}</span>
              <span>{c.restart}</span>
            </button>
          </div>
        </div>
        <div className="mw-workspace">
          <aside className="mw-sidebar" aria-label={c.demoNavigation}>
            <div className="mw-project">
              <div className="mw-project-icon">
                <img src="/landing-demo/cookie.png" alt="" width="40" height="40" />
              </div>
              <div>
                <span>{c.exampleBusiness}</span>
                <strong>{'Nurtelia'}</strong>
              </div>
            </div>
            <nav aria-label={c.previewProgress}>
              <ol className="mw-steps">
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      go(0);
                    }}
                    disabled={0 > furthest}
                    className={`step${step > 0 ? ' done' : ''}`}
                    aria-current={step === 0 ? 'step' : undefined}
                  >
                    <b aria-hidden="true">{'01'}</b>
                    <span>{c.link}</span>
                    <span className="mw-step-check" aria-hidden="true">
                      {'✓'}
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      go(1);
                    }}
                    disabled={1 > furthest}
                    className={`step${step > 1 ? ' done' : ''}`}
                    aria-current={step === 1 ? 'step' : undefined}
                  >
                    <b aria-hidden="true">{'02'}</b>
                    <span>{c.business}</span>
                    <span className="mw-step-check" aria-hidden="true">
                      {'✓'}
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      go(2);
                    }}
                    disabled={2 > furthest}
                    className={`step${step > 2 ? ' done' : ''}`}
                    aria-current={step === 2 ? 'step' : undefined}
                  >
                    <b aria-hidden="true">{'03'}</b>
                    <span>{c.videos}</span>
                    <span className="mw-step-check" aria-hidden="true">
                      {'✓'}
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      go(3);
                    }}
                    disabled={3 > furthest}
                    className={`step${step > 3 ? ' done' : ''}`}
                    aria-current={step === 3 ? 'step' : undefined}
                  >
                    <b aria-hidden="true">{'04'}</b>
                    <span>{c.product}</span>
                    <span className="mw-step-check" aria-hidden="true">
                      {'✓'}
                    </span>
                  </button>
                </li>
                <li>
                  <button
                    type="button"
                    onClick={() => {
                      go(4);
                    }}
                    disabled={4 > furthest}
                    className={`step${step > 4 ? ' done' : ''}`}
                    aria-current={step === 4 ? 'step' : undefined}
                  >
                    <b aria-hidden="true">{'05'}</b>
                    <span>{c.result}</span>
                    <span className="mw-step-check" aria-hidden="true">
                      {'✓'}
                    </span>
                  </button>
                </li>
              </ol>
            </nav>
          </aside>
          <div className="mw-main">
            <div className="mw-subnav">
              <div>
                <button
                  className="mw-utility"
                  id="workspace-back"
                  aria-label={c.backLabel}
                  type="button"
                  onClick={() => {
                    go(step - 1);
                  }}
                  hidden={step === 0}
                >
                  <span className="mw-back-arrow" aria-hidden="true">
                    {'←'}
                  </span>
                  <span>{c.backLabel}</span>
                </button>
                <span
                  className="mw-counter"
                  id="workspace-count"
                  aria-label={`${c.stepLabel} ${String(step + 1)} / 5`}
                >
                  {String(step + 1).padStart(2, '0')} / 05
                </span>
              </div>
              <button
                className="mw-utility"
                id="skip-result"
                type="button"
                onClick={() => {
                  go(4);
                }}
                hidden={step === 4}
              >
                <span>{c.skipToResult}</span>
                <span aria-hidden="true">{'↗'}</span>
              </button>
            </div>
            <BusinessLinkStage language={language} step={step} go={go} />
            <BusinessProfileStage language={language} step={step} go={go} />
            <VideoChoiceStage language={language} step={step} go={go} media={media} />
            <ProductImageStage
              language={language}
              step={step}
              go={go}
              selected={selected}
              dragging={dragging}
              setSelected={setSelected}
              setDragging={setDragging}
              startDrag={startDrag}
              dragOver={dragOver}
              dropProduct={dropProduct}
            />
            <RemadeVideoStage
              language={language}
              step={step}
              go={go}
              media={media}
              view={view}
              setView={setView}
              onRequestAccess={onRequestAccess}
            />

            <p
              id="media-error"
              className="media-error"
              role="alert"
              hidden={!media.error}
            >
              {c.mediaError}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
